import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Award, Video } from "lucide-react";
import { api } from "@/lib/api";
import { IMAGES } from "@/data/site";

export default function PlayerProfile() {
  const { slug } = useParams();
  const [player, setPlayer] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPlayer(null); setNotFound(false);
    api.get(`/players/${slug}`)
      .then((r) => setPlayer(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <section className="pt-40 pb-24 max-w-[1400px] mx-auto px-6 md:px-10 text-center">
        <div className="section-eyebrow mb-6">Erro</div>
        <h1 className="font-cinzel uppercase text-white text-4xl mb-6">Jogador não encontrado</h1>
        <Link to="/jogadores" className="btn-outline"><ArrowLeft size={14} /> Voltar</Link>
      </section>
    );
  }

  if (!player) {
    return <div className="pt-40 pb-24 text-center text-neutral-500">A carregar...</div>;
  }

  return (
    <>
      {/* HERO */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <img src={player.photo || IMAGES.athletePortrait} alt={player.name} className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0B]/70 via-[#0B0B0B]/85 to-[#0B0B0B]" />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-12 items-end">
          <div className="lg:col-span-2 relative aspect-[3/4] overflow-hidden">
            <img src={player.photo || IMAGES.athletePortrait} alt={player.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 border border-[#A67C00]/30" />
          </div>
          <div className="lg:col-span-3">
            <Link to="/jogadores" className="section-eyebrow mb-6 inline-flex items-center gap-2 hover:text-white">
              <ArrowLeft size={12} /> Voltar aos jogadores
            </Link>
            <div className="text-[#A67C00] text-sm tracking-[0.3em] uppercase mt-6 mb-4">{player.position}</div>
            <h1 className="font-cinzel uppercase text-white text-5xl md:text-6xl lg:text-7xl font-bold leading-none tracking-tight mb-8">
              {player.name}
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5 border border-white/5">
              {[
                ["Nacionalidade", player.nationality],
                ["Escalão", player.age_group],
                ["Clube atual", player.current_club],
                ["Data de nascimento", player.birth_date],
                ["Altura", player.height_cm ? `${player.height_cm} cm` : "-"],
                ["Peso", player.weight_kg ? `${player.weight_kg} kg` : "-"],
                ["Pé dominante", player.dominant_foot],
                ["Posição secundária", player.secondary_position],
              ].map(([k, v]) => (
                <div key={k} className="bg-[#0B0B0B] p-4">
                  <div className="text-neutral-500 text-[0.6rem] tracking-[0.3em] uppercase mb-2">{k}</div>
                  <div className="text-white text-sm">{v || "-"}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PERFIL */}
      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12">
          <div>
            <div className="section-eyebrow mb-6">Perfil do jogador</div>
          </div>
          <div className="md:col-span-2">
            <p className="text-neutral-300 text-lg leading-relaxed">{player.description}</p>
          </div>
        </div>
      </section>

      {/* CARACTERÍSTICAS */}
      {player.characteristics && Object.keys(player.characteristics).length > 0 && (
        <section className="py-24 md:py-32 bg-[#0F0F0F] border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12">
            <div>
              <div className="section-eyebrow mb-6">Características</div>
              <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight">Perfil técnico e físico</h2>
            </div>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6">
              {Object.entries(player.characteristics).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between mb-2">
                    <span className="text-white text-sm">{k}</span>
                    <span className="text-[#A67C00] text-sm font-cinzel">{v}</span>
                  </div>
                  <div className="h-[2px] bg-white/10 relative">
                    <div className="absolute inset-y-0 left-0 bg-[#A67C00]" style={{ width: `${v}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* HISTÓRICO */}
      {player.history?.length > 0 && (
        <section className="py-24 md:py-32 border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12">
            <div>
              <div className="section-eyebrow mb-6">Histórico</div>
              <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight">Percurso desportivo</h2>
            </div>
            <div className="md:col-span-2">
              <div className="border-l border-[#A67C00]/40 pl-8 space-y-8">
                {player.history.map((h) => (
                  <div key={`${h.club}-${h.seasons}`} className="relative">
                    <div className="absolute -left-[35px] top-2 w-2 h-2 bg-[#A67C00]" />
                    <div className="text-[#A67C00] text-xs tracking-[0.25em] uppercase mb-1">{h.seasons}</div>
                    <div className="font-cinzel uppercase text-white text-xl mb-1">{h.club}</div>
                    <div className="text-neutral-400 text-sm">{h.level}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ESTATÍSTICAS */}
      {player.stats && Object.keys(player.stats).length > 0 && (
        <section className="py-24 md:py-32 bg-[#0F0F0F] border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="section-eyebrow mb-6">Estatísticas</div>
            <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight mb-12">Números da carreira</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-white/5 border border-white/5">
              {Object.entries(player.stats).map(([k, v]) => (
                <div key={k} className="bg-[#0B0B0B] p-6">
                  <div className="text-neutral-500 text-[0.6rem] tracking-[0.3em] uppercase mb-2">{k.replace(/_/g, " ")}</div>
                  <div className="font-cinzel text-[#A67C00] text-3xl md:text-4xl">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CONQUISTAS */}
      {player.achievements?.length > 0 && (
        <section className="py-24 md:py-32 border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-12">
            <div>
              <Award size={28} className="text-[#A67C00] mb-6" strokeWidth={1.2} />
              <div className="section-eyebrow mb-6">Conquistas</div>
              <h2 className="font-cinzel uppercase text-white text-3xl md:text-4xl tracking-tight">Distinções</h2>
            </div>
            <div className="md:col-span-2">
              <ul className="space-y-4">
                {player.achievements.map((a, i) => (
                  <li key={a} className="flex items-start gap-4 border-b border-white/5 pb-4">
                    <span className="font-cinzel text-[#A67C00] text-sm">{String(i + 1).padStart(2, "0")}</span>
                    <span className="text-white text-base">{a}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* VÍDEOS */}
      {player.videos?.length > 0 && (
        <section className="py-24 md:py-32 bg-[#0F0F0F] border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <Video size={28} className="text-[#A67C00] mb-6" strokeWidth={1.2} />
            <div className="section-eyebrow mb-6">Vídeos</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {player.videos.map((v, i) => (
                <div key={v} className="aspect-video">
                  <iframe src={v} title={`Vídeo ${i + 1}`} className="w-full h-full" allowFullScreen />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* GALERIA */}
      {player.gallery?.length > 0 && (
        <section className="py-24 md:py-32 border-b border-white/5">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="section-eyebrow mb-6">Galeria</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {player.gallery.map((g, i) => (
                <div key={g} className="relative aspect-square overflow-hidden zoom-parent">
                  <img src={g} alt={`Galeria ${i + 1}`} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <div className="section-eyebrow mb-6">Interessado?</div>
          <h2 className="font-cinzel uppercase text-white text-3xl md:text-5xl tracking-tight mb-10">
            Solicitar informações sobre <br /><span className="text-[#A67C00]">{player.name}</span>
          </h2>
          <Link
            to={`/contactos?jogador=${encodeURIComponent(player.name)}`}
            data-testid="player-request-info"
            className="btn-gold"
          >
            Solicitar informações <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </>
  );
}
